// Phase 14 slice 1 — Ownership Dashboard.
//
// Budget vs actual by category + total + cost-per-acre. Water row auto-
// populates from water_budgets.used_gallons × course.water_cost_per_1000_gal.
// Ownership + ageye_admin can edit the table inline; everyone else reads.

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { UserRole } from '@greeniq/shared';
import { ApiError, api } from '../api/client.js';
import { useMe } from '../context/MeContext.js';

const WRITE_ROLES: UserRole[] = ['ageye_admin', 'ownership'];

const CATEGORIES = ['chemicals', 'fertilizer', 'water', 'labor', 'equipment', 'capex', 'other'] as const;
type Category = typeof CATEGORIES[number];

interface BudgetRow {
  id: number;
  courseId: number;
  year: number;
  category: string;
  budgetedCents: string | number;
  actualCents: string | number;
  notes: string | null;
  updatedAt?: string;
}

interface DashboardResponse {
  year: number;
  acreage: number | null;
  waterCostPer1000GalCents: number | null;
  rows: BudgetRow[];
  totals: {
    budgetedCents: number;
    actualCents: number;
    costPerAcreCents: number | null;
    pctUsed: number | null;
  };
}

export function OwnershipPage() {
  const { courseSlug } = useParams();
  const { me } = useMe();
  const role: UserRole | undefined = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const canEdit = role ? WRITE_ROLES.includes(role) : false;

  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!courseSlug) return;
    try {
      const r = await api.get<DashboardResponse>(`/courses/${courseSlug}/ownership/dashboard?year=${year}`);
      setData(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug, year]);

  if (error) return <div className="p-8 text-[color:var(--color-error)]">{error}</div>;
  if (!data) return <div className="p-8 text-fg-muted">Loading…</div>;

  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i);

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="uppercase tracking-wider text-xs text-greeniq-400 font-semibold">Phase 14</p>
          <h1 className="mt-1 text-3xl md:text-4xl font-display font-bold text-fg">Ownership Dashboard</h1>
          <p className="mt-1 text-fg-muted">
            Budget vs. actual by category. Water actuals auto-track from irrigation prescription runtimes × your water cost.
          </p>
        </div>
        <label className="text-xs">
          Year
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="ml-2 border border-border rounded-md py-1.5 px-2 text-sm"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-8 grid md:grid-cols-4 gap-3">
        <Tile
          label="Total budget"
          value={fmtDollars(data.totals.budgetedCents)}
          detail={`${data.year}`}
        />
        <Tile
          label="Total actual YTD"
          value={fmtDollars(data.totals.actualCents)}
          detail={data.totals.pctUsed !== null
            ? `${(data.totals.pctUsed * 100).toFixed(1)}% of budget`
            : 'no budget set'}
          danger={data.totals.pctUsed !== null && data.totals.pctUsed > 1}
        />
        <Tile
          label="Cost / acre"
          value={data.totals.costPerAcreCents !== null
            ? fmtDollars(data.totals.costPerAcreCents)
            : '—'}
          detail={data.acreage !== null ? `${data.acreage.toFixed(1)} ac total` : 'acreage not set'}
        />
        <WaterCostTile
          courseSlug={courseSlug ?? ''}
          canEdit={canEdit}
          waterCostCents={data.waterCostPer1000GalCents}
          onSaved={load}
        />
      </div>

      <section className="mt-8 bg-bg-card rounded-lg border border-border overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border bg-bg-elevated">
              <th className="px-3 py-2 font-semibold text-fg-muted">Category</th>
              <th className="px-3 py-2 font-semibold text-fg-muted text-right">Budgeted</th>
              <th className="px-3 py-2 font-semibold text-fg-muted text-right">Actual</th>
              <th className="px-3 py-2 font-semibold text-fg-muted text-right">% used</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">Notes</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORIES.map((cat) => {
              const row = data.rows.find((r) => r.category === cat);
              return (
                <BudgetTableRow
                  key={cat}
                  courseSlug={courseSlug ?? ''}
                  year={data.year}
                  category={cat}
                  row={row ?? null}
                  canEdit={canEdit}
                  onSaved={load}
                />
              );
            })}
          </tbody>
        </table>
      </section>

      <p className="mt-4 text-xs text-fg-muted">
        Water actual is auto-computed from this year's irrigation prescription total × water-cost setting. QuickBooks sync, per-round cost, sustainability metrics, and a board-presentation PDF land in Phase 14 slice 2.
      </p>
    </div>
  );
}

function Tile({
  label,
  value,
  detail,
  danger = false,
}: {
  label: string;
  value: string;
  detail: string;
  danger?: boolean;
}) {
  return (
    <div className={`p-4 rounded-lg border ${danger ? 'border-red-300 bg-error-muted' : 'border-border bg-bg-card'}`}>
      <div className="text-xs uppercase tracking-wider text-fg-muted">{label}</div>
      <div className={`mt-1 text-2xl font-display font-semibold ${danger ? 'text-[color:var(--color-error)]' : 'text-greeniq-300'}`}>
        {value}
      </div>
      <div className="mt-1 text-xs text-fg-muted">{detail}</div>
    </div>
  );
}

function WaterCostTile({
  courseSlug,
  canEdit,
  waterCostCents,
  onSaved,
}: {
  courseSlug: string;
  canEdit: boolean;
  waterCostCents: number | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftDollars, setDraftDollars] = useState('');

  useEffect(() => {
    setDraftDollars(waterCostCents !== null ? (waterCostCents / 100).toFixed(2) : '');
  }, [waterCostCents]);

  async function save() {
    const cents = Math.round(Number(draftDollars) * 100);
    if (!Number.isFinite(cents) || cents < 0) return;
    try {
      await api.put(`/courses/${courseSlug}/ownership/water-cost`, {
        waterCostPer1000GalCents: cents,
      });
      setEditing(false);
      onSaved();
    } catch {
      /* inline error is fine to skip; audit + reload via onSaved */
    }
  }

  return (
    <div className="p-4 rounded-lg border border-border bg-bg-card">
      <div className="text-xs uppercase tracking-wider text-fg-muted">Water cost</div>
      {editing ? (
        <div className="mt-1 flex items-center gap-1">
          <span className="text-sm text-fg-muted">$</span>
          <input
            type="number"
            step="0.01"
            value={draftDollars}
            onChange={(e) => setDraftDollars(e.target.value)}
            className="w-24 border border-border rounded-md py-1 px-2 text-lg"
          />
          <button
            onClick={() => void save()}
            className="ml-1 px-2 py-1 text-xs rounded-md bg-greeniq-500 text-[#0b1014] font-semibold"
          >
            Save
          </button>
        </div>
      ) : (
        <div className="mt-1 text-2xl font-display font-semibold text-greeniq-300">
          {waterCostCents !== null ? `$${(waterCostCents / 100).toFixed(2)}` : '—'}
        </div>
      )}
      <div className="mt-1 text-xs text-fg-muted flex items-center gap-2">
        per 1,000 gal
        {canEdit && !editing && (
          <button onClick={() => setEditing(true)} className="text-greeniq-400 hover:underline text-xs">
            Edit
          </button>
        )}
      </div>
    </div>
  );
}

function BudgetTableRow({
  courseSlug,
  year,
  category,
  row,
  canEdit,
  onSaved,
}: {
  courseSlug: string;
  year: number;
  category: Category;
  row: BudgetRow | null;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftBudget, setDraftBudget] = useState('');
  const [draftActual, setDraftActual] = useState('');
  const [draftNotes, setDraftNotes] = useState('');

  const budgetedCents = row ? Number(row.budgetedCents) : 0;
  const actualCents = row ? Number(row.actualCents) : 0;
  const pct = budgetedCents > 0 ? (actualCents / budgetedCents) * 100 : null;

  useEffect(() => {
    setDraftBudget((budgetedCents / 100).toFixed(2));
    setDraftActual((actualCents / 100).toFixed(2));
    setDraftNotes(row?.notes ?? '');
  }, [budgetedCents, actualCents, row]);

  async function save() {
    try {
      await api.put(`/courses/${courseSlug}/ownership/budget`, {
        year,
        category,
        budgetedCents: Math.round(Number(draftBudget) * 100),
        actualCents: Math.round(Number(draftActual) * 100),
        notes: draftNotes || null,
      });
      setEditing(false);
      onSaved();
    } catch {
      /* skip — audit captures errors */
    }
  }

  const isWater = category === 'water';

  return (
    <tr className="border-b border-border last:border-b-0 align-top">
      <td className="px-3 py-2 capitalize font-semibold">{category}</td>
      {editing ? (
        <>
          <td className="px-3 py-2 text-right">
            <input
              type="number"
              step="0.01"
              value={draftBudget}
              onChange={(e) => setDraftBudget(e.target.value)}
              className="w-28 border border-border rounded-md py-1 px-2 text-right"
            />
          </td>
          <td className="px-3 py-2 text-right">
            {isWater ? (
              <span className="text-xs text-fg-muted">auto</span>
            ) : (
              <input
                type="number"
                step="0.01"
                value={draftActual}
                onChange={(e) => setDraftActual(e.target.value)}
                className="w-28 border border-border rounded-md py-1 px-2 text-right"
              />
            )}
          </td>
          <td className="px-3 py-2 text-right">—</td>
          <td className="px-3 py-2">
            <input
              type="text"
              value={draftNotes}
              onChange={(e) => setDraftNotes(e.target.value)}
              className="w-full border border-border rounded-md py-1 px-2"
            />
            <div className="mt-1 flex gap-2">
              <button onClick={() => void save()} className="text-xs text-greeniq-400 hover:underline">Save</button>
              <button onClick={() => setEditing(false)} className="text-xs text-fg-muted hover:text-fg">Cancel</button>
            </div>
          </td>
        </>
      ) : (
        <>
          <td className="px-3 py-2 text-right font-mono">{fmtDollars(budgetedCents)}</td>
          <td className="px-3 py-2 text-right font-mono">
            {fmtDollars(actualCents)}
            {isWater && <span className="ml-1 text-[10px] text-fg-muted">auto</span>}
          </td>
          <td className={`px-3 py-2 text-right text-xs ${pct !== null && pct > 100 ? 'text-[color:var(--color-error)] font-semibold' : 'text-fg-muted'}`}>
            {pct !== null ? `${pct.toFixed(0)}%` : '—'}
          </td>
          <td className="px-3 py-2 text-fg-muted max-w-xs">
            {row?.notes ?? ''}
            {canEdit && (
              <button
                onClick={() => setEditing(true)}
                className="ml-2 text-xs text-greeniq-400 hover:underline"
              >
                Edit
              </button>
            )}
          </td>
        </>
      )}
    </tr>
  );
}

function fmtDollars(cents: number | string): string {
  const n = typeof cents === 'number' ? cents : Number(cents);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}
